import React, {useState} from 'react';
import {toAbsoluteUrl} from '../../../../../../../_metronic/_helpers';
import {Button, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import moment from 'moment';
import {useDispatch} from 'react-redux';
import {FormikProvider, useFormik} from 'formik';
import classnames from 'classnames';
import {TypedDataItemSelect} from '../../../../../_common/components/DataItemSelect';
import {TypedDataItem} from '../../../../../_common/components/DataItem';
import UnitGrid from './UnitGrid';
import can from '../../../../../_common/components/Permissions/can';
import * as actions from '../../../../_redux/invoicesActions';
import {currencyListOptions, paymentTypeOptions} from '../../../../../_common/constants/options';
import {existingReceiptDataSelectStyles} from '../../../../../_common/styles/reactSelectStyles';
import {floatValueSign} from '../../../../../../../utils';
import {useInvoicesUIContext} from "../../../InvoicesUIContext";

interface Props {
  _id: string
  agentId: number
  agentName: string
  amount: number
  customerEmail: string
  customerID: string
  customerName: string
  status: number
  descriptions: string[]
}

type Option = { value: number, label: string };
type Line = {
  agentID: number,
  description: string,
  paymentType: Option,
  dueDate: number,
  checkNumber: string | null,
  bankCode: string | null,
  branchNumber: string | null,
  accountNumber: string,
  amount: number | null,
};

type FormikValues = {
  customerName: string,
  customerID: string | null,
  customerEmail: string,
  agentID: number,
  currencyID: Option,
  exchangeRate: number,
  comment: string,
  lines: Line[],
}

const PopupConvertToType2 = ({
                               _id,
                               agentId,
                               agentName,
                               amount,
                               descriptions,
                               customerEmail,
                               customerID,
                               customerName,
                               status
                             }: Props) => {
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const handleCloseModal = () => {
    formik.resetForm();
    setShow(false);
  };
  const handleShowModal = () => setShow(true);

  const isEnabled = can('full', 'invoices') && status === 1;

  const {formatMessage} = useIntl();
  const buttonOpen = formatMessage({id: 'invoices-button-convert-to-2', defaultMessage: 'Convert to type 2'});
  const header = formatMessage({
    id: 'invoices-popup-convert-to-8-message',
    defaultMessage: 'Create invoice of type 8 from {id}',
  }, {id: _id});
  const buttonCancel = formatMessage({id: 'label-button-cancel', defaultMessage: 'Cancel'});
  const buttonConfirm = formatMessage({id: 'label-button-send', defaultMessage: 'Send'});
  const exchangeRateLabel = formatMessage({id: 'receipts-popup-exchange-rate', defaultMessage: 'Exchange rate'});
  const commentLabel = formatMessage({id: 'receipts-popup-existing-comment', defaultMessage: 'Comment'});
  const currencyLabel = formatMessage({id: 'receipts-popup-currency', defaultMessage: 'Currency'});

  const buttonClassName = classnames(
    `w-100 px-4 py-2 d-flex align-items-center justify-content-between text-nowrap font-size-lg`,
    {[`opacity-30`]: !isEnabled}
  );
  const commonContainerStyle = 'd-flex justify-content-between px-2';
  const commonSideLabelStyle = 'text-nowrap font-size-lg custom-brand-color mr-2';

  const preparedData = (values: FormikValues) => {
    const {
      customerID,
      customerEmail,
      agentID,
      currencyID,
      exchangeRate,
      comment,
      lines
    } = values;

    const linesData = (Array.isArray(lines)) && lines.map((item) => {
      return {
        description: item.description || '',
        paymentType: item.paymentType ? parseInt(item.paymentType.value.toString()) : null,
        dueDate: item.dueDate
          ? Date.parse(moment(item.dueDate).format('YYYY-MM-DD'))
          : Date.parse(moment().format('YYYY-MM-DD')),
        checkNumber: item.checkNumber ? parseInt(item.checkNumber) : null,
        bankCode: item.bankCode ? parseInt(item.bankCode) : null,
        branchNumber: item.branchNumber ? parseInt(item.branchNumber) : null,
        accountNumber: item.accountNumber || '',
        amount: item.amount ? parseFloat(item.amount.toString()) : null,
      };
    });

    return {
      invoiceID: _id,
      agentID,
      customerID,
      customerEmail,
      currencyID: currencyID.value ? parseInt(currencyID.value.toString(), 10) : null,
      exchangeRate: parseInt(exchangeRate.toString(), 10) || null,
      comment: comment || '',
      lines: linesData || [],
      customerName,
      agentName
    };
  };

  const formik = useFormik<FormikValues>({
    initialValues: {
      customerName: customerName || '',
      customerID: customerID || null,
      customerEmail: customerEmail || '',
      agentID: agentId,
      currencyID: {value: 1, label: 'ש"ח'},
      exchangeRate: 1,
      comment: '',
      lines: [{
        agentID: agentId,
        description: descriptions ? descriptions.filter(Boolean).join(' ') : '',
        paymentType: paymentTypeOptions.filter(item => item.value === 9)[0],
        dueDate: Date.parse(moment().format('YYYY-MM-DD')),
        checkNumber: null,
        bankCode: null,
        branchNumber: null,
        accountNumber: '',
        amount: amount || null,
      }]
    },
    onSubmit: () => {
    },
    validate: (values: FormikValues) => {
      let errors: { [p: string]: string } = {};
      const {lines} = values;

      if (!values.currencyID) errors['currencyID'] = 'Required';
      if (!values.exchangeRate) errors['exchangeRate'] = 'Required';

      if (values.lines.length === 0) {
        errors.lines = 'Required at least one line';
      }

      lines.forEach((item) => {
        if (!item.accountNumber && (item.paymentType.value === 1)) errors['accountNumber'] = 'Required line item';
        if (!item.amount) errors['amount'] = 'Required line item';
        if (!item.bankCode && (item.paymentType.value === 1)) errors['bankCode'] = 'Required line item';
        if (!item.branchNumber && (item.paymentType.value === 1)) errors['branchNumber'] = 'Required line item';
        if (!item.checkNumber && (item.paymentType.value === 1)) errors['checkNumber'] = 'Required line item';
        if (!item.paymentType) errors['paymentType'] = 'Required line item';
      });

      return errors;
    },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const {queryParams} = useInvoicesUIContext();

  const handleConfirmClick = async () => {
    try {
      dispatch(actions.convertToType2(preparedData(formik.values)));
      setTimeout(() => {
        dispatch(actions.getInvoices(queryParams));
        handleCloseModal();
      }, 1500)
    } finally {
      handleCloseModal();
    }
  };

  return (
    <>
      <div
        className={buttonClassName}
        onClick={() => isEnabled && handleShowModal()}
      >
        {buttonOpen}
        <img
          src={toAbsoluteUrl('/media/svg/Convert_brandColor.svg')}
          alt="icon" className={'w-30px h-30px ml-2'}
        />
      </div>
      <Modal show={show} onHide={handleCloseModal} className="popupAddReceiptContainer">
        <Modal.Body>
          <FormikProvider value={formik}>
            <h3 className="card-title font-weight-bold text-dark text-center mb-4">
              {header}
            </h3>
            <div className="position-relative">
              <div className="d-flex mx-auto justify-content-between px-8 my-8">
                <TypedDataItemSelect
                  fieldId={'currencyID'}
                  placeholder={'בחירה...'}
                  options={currencyListOptions}
                  customStyles={existingReceiptDataSelectStyles}
                  containerStyle={commonContainerStyle}
                  labelStyle={commonSideLabelStyle}
                  inputContainerStyle={'w-150px'}
                  label={
                    <div className={'position-relative'}>
                      <span className={'mandatoryMark'}>*</span>
                      {currencyLabel}
                    </div>
                  }
                />
                <TypedDataItem
                  fieldId={'exchangeRate'}
                  onChange={floatValueSign}
                  containerStyle={commonContainerStyle}
                  sideLabelStyle={commonSideLabelStyle}
                  inputContainerStyle={'w-150px'}
                  sideLabel={
                    <div className={'position-relative'}>
                      <span className={'mandatoryMark'}>*</span>
                      {exchangeRateLabel}
                    </div>
                  }
                />
                <TypedDataItem
                  fieldId={'comment'}
                  sideLabel={commentLabel}
                  containerStyle={commonContainerStyle}
                  sideLabelStyle={commonSideLabelStyle}
                  inputContainerStyle={'w-500px'}
                />
              </div>
              <UnitGrid values={formik.values}/>
            </div>
            <div className="d-flex justify-content-center mt-8 mx-auto w-50">
              <Button
                type={'button'}
                className="btn btn-secondary btn-sm col-2 mx-3 "
                onClick={handleCloseModal}
              >
                {buttonCancel}
              </Button>
              <Button
                className="btn btn-icon btn-sm col-2 mx-3 custom-send-button"
                type="submit"
                disabled={!formik.isValid}
                onClick={handleConfirmClick}
              >
                {buttonConfirm}
              </Button>
            </div>
          </FormikProvider>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PopupConvertToType2;